// useFileBatch.js
// The batch a file is being checked in (users/{cid}/tables/{tid}/batches/{bid},
// Spec 3 section 1.4), live, and the names of the files checked with it.
//
// The batch document holds only the member file ids, so the names are read from
// those files' documents with one query whenever the member list changes. A
// completed batch is not followed: once a file has landed, its batch says nothing
// a card or the review dialog shows.
//
// Returns { batch, fileNames, error }:
//   batch      the batch document ({ id, ...data }), or null while it loads or
//              when the file is in no batch that is still open
//   fileNames  { fileId: name } for every member whose document was found
//   error      true when the batch could not be read

import { useEffect, useState } from 'react';
import { collection, doc, documentId, getDocs, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../services/firebase';

export default function useFileBatch(companyId, tableId, file) {
  const batchId = file?.batch_id && file.batch_status !== 'completed' ? file.batch_id : null;

  // Each piece of state remembers which batch (or member list) it belongs to, so
  // a response for a batch the file has since left is never shown.
  const [batchState, setBatchState] = useState({ batchId: null, batch: null, error: false });
  useEffect(() => {
    if (!companyId || !tableId || !batchId) return undefined;
    const batchRef = doc(db, `users/${companyId}/tables/${tableId}/batches/${batchId}`);
    return onSnapshot(batchRef, (snapshot) => {
      setBatchState({
        batchId,
        batch: snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null,
        error: false,
      });
    }, (error) => {
      console.error('Error watching the batch a file is checked in:', error);
      setBatchState({ batchId, batch: null, error: true });
    });
  }, [companyId, tableId, batchId]);
  const current = batchState.batchId === batchId ? batchState : { batch: null, error: false };
  const batch = batchId ? current.batch : null;

  const memberIds = batch?.files || [];
  const membersKey = JSON.stringify([...memberIds].sort());
  const [namesState, setNamesState] = useState({ membersKey: null, fileNames: {} });
  useEffect(() => {
    const ids = JSON.parse(membersKey);
    if (!companyId || !tableId || ids.length === 0) return undefined;
    let cancelled = false;
    const membersQuery = query(
      collection(db, `users/${companyId}/tables/${tableId}/files`),
      where(documentId(), 'in', ids),
    );
    getDocs(membersQuery).then((snapshot) => {
      if (cancelled) return;
      const fileNames = {};
      snapshot.docs.forEach((member) => { fileNames[member.id] = member.data().name; });
      setNamesState({ membersKey, fileNames });
    }).catch((error) => {
      console.error('Error reading the names of the files in a batch:', error);
    });
    return () => { cancelled = true; };
  }, [companyId, tableId, membersKey]);
  const fileNames = namesState.membersKey === membersKey ? namesState.fileNames : {};

  return { batch, fileNames, error: Boolean(batchId) && current.error };
}
